// getMetaTagsForHelmet.js
'use strict';

import logoSource from '../../img/share.jpg';

/**
 * Generate meta tags for React Helmet (following RRv7 pattern) https://api.reactrouter.com/v7/types/react_router.MetaDescriptor.html
 * @param {object} params
 * @param {string} params.title - Page title
 * @param {string} params.description - Page description
 * @param {Array<string>} params.keywords - Array of keywords
 * @param {Array<{url: string, width?: number, height?: number}>} params.images - Array of image objects with url, width, and height
 * @param {string} params.hostname - Hostname for generating image URLs
 * @param {string} params.ogType - OG type (default: 'website')
 * @param {string} params.url - OG URL for the page
 * @param {string} params.siteName - OG site name (default: 'SWAG')
 * @param {string} params.twitterCard - Twitter card type (default: 'summary_large_image')
 * @param {object} params.alternates - Alternate links (canonical + alternates must be managed together)
 * @param {string} params.alternates.canonical - Canonical URL for the page
 * @param {Array} params.alternates.alternates - Array of alternate language links
 * @param {Array<string>} params.robots - Array of robots directives (e.g., ['index', 'follow'], ['noindex', 'nofollow'])
 * @param {object|Array} params.jsonLd - JSON-LD structured data object or array
 * @param {number} params.videoDuration - Video duration in seconds (optional, for video pages)
 * @param {object} params.videoActor - Video actor metadata (optional, for video pages with ogType: 'video.other')
 * @param {string} params.videoActor.id - Actor/creator user ID
 * @param {string} params.videoActor.username - Actor/creator username
 * @param {string} params.videoActor.url - Actor/creator profile URL
 * @param {string} params.videoActor.tag - Video tag/serial code (optional)
 * @returns {Array} Array of head tag objects for RouteMetaTags
 */
const getMetaTagsForHelmet = ({
  // basic meta
  title,
  description,
  keywords = [],
  images = [],
  hostname = '',

  // Open Graph
  ogType = 'website',
  url = null,
  siteName = 'SWAG',

  // Twitter
  twitterCard = 'summary_large_image',

  // Alternates (canonical + alternates must be managed together)
  alternates = {},

  // Robots
  robots = [],

  // json-ld
  jsonLd = null,

  // Video-specific (optional)
  videoDuration = null,
  videoActor = null,
} = {}) => {
  const { canonical = null, alternates: alternateLinks = null } = alternates;
  const head = [];

  // Title
  if (title) {
    head.push({ title });
    head.push({ property: 'og:title', content: title });
  }

  // Description
  if (description) {
    head.push({ name: 'description', content: description });
    head.push({ property: 'og:description', content: description });
  }

  // Keywords
  if (keywords && keywords.length > 0) {
    head.push({ name: 'keywords', content: keywords.join(', ') });
  }

  // OG Type
  head.push({ property: 'og:type', content: ogType });

  // OG URL
  if (url) {
    head.push({ property: 'og:url', content: url });
  }

  // OG Site Name
  if (siteName) {
    head.push({ property: 'og:site_name', content: siteName });
  }

  // Twitter meta tags
  const twitterFields = [
    { name: 'twitter:card', value: twitterCard },
    { name: 'twitter:site', value: '@swaglive_app' },
    { name: 'twitter:title', value: title },
    { name: 'twitter:description', value: description },
  ];

  twitterFields.forEach(({ name, value }) => {
    if (value) {
      head.push({ name, content: value });
    }
  });

  // Images
  const imagesToProcess =
    images.length > 0
      ? images
      : [
          {
            url: hostname ? `https://${hostname}${logoSource}` : logoSource,
            width: 1600,
            height: 900,
          },
        ];

  imagesToProcess.forEach((image, index) => {
    const { url, width, height } = image;

    // All images use og:image with width/height
    head.push({ property: 'og:image', content: url });

    // First image also gets Twitter tag
    if (index === 0) {
      head.push({ property: 'twitter:image', content: url });
    }

    if (width) {
      head.push({ property: 'og:image:width', content: String(width) });
    }

    if (height) {
      head.push({ property: 'og:image:height', content: String(height) });
    }
  });

  // JSON-LD structured data
  if (jsonLd) {
    head.push({ 'script:ld+json': jsonLd });
  }

  // Canonical URL
  if (canonical) {
    head.push({ tagName: 'link', rel: 'canonical', href: canonical });
  }

  // Alternate language links
  if (alternateLinks && Array.isArray(alternateLinks)) {
    alternateLinks.forEach(alternate => {
      if (alternate.href && alternate.hreflang) {
        head.push({
          tagName: 'link',
          rel: 'alternate',
          // camelCase: descriptors are rendered as React elements (RouteMetaTags)
          hrefLang: alternate.hreflang,
          href: alternate.href,
        });
      }
    });
  }

  // Robots meta tag
  if (robots && Array.isArray(robots) && robots.length > 0) {
    head.push({ name: 'robots', content: robots.join(', ') });
  }

  // Video metadata (for video.other type)
  // Note: og:video tags are intentionally not included here because:
  // 1. og:video should point to actual video file URLs (.mp4, etc.), not page URLs
  // 2. og:video:type should be video MIME types (video/mp4), not 'text/html'
  // 3. For video pages, use ogType: 'video.other' instead
  if (videoDuration) {
    head.push({
      property: 'og:video:duration',
      content: String(videoDuration),
    });
  }

  if (videoActor) {
    const actorFields = [
      { key: 'id', property: 'og:video:actor:id' },
      { key: 'username', property: 'og:video:actor:username' },
      { key: 'url', property: 'og:video:actor:url' },
      { key: 'tag', property: 'og:video:tag' },
    ];

    actorFields.forEach(({ key, property }) => {
      const value = videoActor[key];
      if (value) {
        head.push({
          property,
          content: value,
        });
      }
    });
  }

  return head;
};

export default getMetaTagsForHelmet;
